/**
 * ScribeHeartbeat
 *
 * This module is reponsible for transmiting basic request statistics from a
 * stats source back to scribe at a set interval.
 *
 * @module    scribe_heartbeat
 * @author    Brian LaMattina <blamattina@wayfair.com>
 * @copyright 2016 Wayfair, LLC - All rights reserved
 */
import _ from 'underscore';
import $ from 'jquery';
import SCRIBE_EVENTS from './scribe_event_constants';
import Visibility from './visibility';

/**
 * Construct a ScribeHeartbeat
 *
 * @class
 * @param {ScribeClient} client a scribe client to transmit data with
 * @param {ScribeStats} statsSource a source of statistics to send; can be any
 * object with a 'getStats' and 'resetStats' method.
 */
class ScribeHeartbeat {
  // Seed to start the heartbeat timer at in ms
  SEED = 10000; // ms
  // The total number of heartbeats we want to send to the client
  TOTAL_HEARTBEAT = 5;

  constructor(client, statsSource) {
    this._client = client;
    this._statsSource = statsSource;
    this._intervalId = null;
    this._heartBeatCount = 0;
    // The interval at which the heartbeat should pulse, it will increment by factors of 2.
    this._interval = this.SEED; // ms
  }

  /**
   * Start sending data at a regular interval
   *
   * @return {$.Deferred} promise that will notify when the heartbeat pulses with
   * the requestPromise of the request to send activity data to Scribe.
   */
  start() {
    this.stop();
    this._deferred = new $.Deferred();
    this._intervalId = Visibility.every(this._interval, this._increaseInterval);
    Visibility.change(this._stopHeartBeat.bind(this));
    return this._deferred.promise();
  }

  /**
   * Stop sending data at a regular interval, intervalIds start at 0
   */
  stop() {
    if (this._intervalId !== null) {
      Visibility.stop(this._intervalId);
    }
    if (this._deferred) {
      this._deferred.resolve();
    }
  }

  /**
   * Stop sending the heartbeats as soon as the page tabs out, but don't resolve the promise.
   * Resume once the person tabs back in with the same hold off they left on.
   *
   * @private
   */
  _stopHeartBeat() {
    // Stop the heartbeat as SOON as the person tabs out
    Visibility.stop(this._intervalId);
    if (
      Visibility.state() === 'visible' &&
      this._heartBeatCount < this.TOTAL_HEARTBEAT
    ) {
      this._intervalId = Visibility.every(
        this._interval,
        this._increaseInterval
      );
    }
  }

  /**
   * This method uses arrow syntax because the function is used as a callback and we need
   * __this__ to be bound to the enclosing class
   *
   * @private
   */
  _increaseInterval = () => {
    // Stop the timer without resolving the promise
    Visibility.stop(this._intervalId);
    // Back off of heartbeat is as follows 10s, 20s, 40s, 80s, 160s
    if (this._heartBeatCount < this.TOTAL_HEARTBEAT) {
      this._sendHeartbeat();
      // Continually increase the interval between heartbeats by factors of 2.
      this._interval = this._interval * 2;
      this._intervalId = Visibility.every(
        this._interval,
        this._increaseInterval
      );
      this._heartBeatCount++;
    }
  };

  _sendHeartbeat() {
    const stats = this._statsSource.getStats();
    const eventData = _.extend({eventType: SCRIBE_EVENTS.HEARTBEAT}, stats);
    this._statsSource.resetStats();
    this._deferred.notify(this._client.sendEvent(eventData));
  }
}

export default ScribeHeartbeat;
